import React, { useState } from 'react';
import { X, Plus, GripVertical, Pencil, Trash2, Check } from 'lucide-react';
import './TabManagerModal.css';
import type { Tab } from './CycleTabs';

interface TabManagerModalProps {
    tabs: Tab[];
    cycleId: string;
    onClose: () => void;
    onRefresh: () => Promise<void>;
    setToast: (t: { message: string; type: 'success' | 'error' | 'info' }) => void;
}

const API = '/api';

const TabManagerModal: React.FC<TabManagerModalProps> = ({
    tabs,
    cycleId,
    onClose,
    onRefresh,
    setToast,
}) => {
    const sorted = [...tabs].sort((a, b) => a.order - b.order);

    // Estado local de abas para reordenação otimista
    const [localTabs, setLocalTabs] = useState(sorted);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editingName, setEditingName] = useState('');
    const [newTabName, setNewTabName] = useState('');
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState('');
    const [saving, setSaving] = useState(false);
    const [dragOver, setDragOver] = useState<string | null>(null);
    const [dragging, setDragging] = useState<string | null>(null);

    const token = () => localStorage.getItem('accessToken') || '';

    // ── Criar aba ──
    const handleCreate = async () => {
        if (!newTabName.trim()) return;
        setSaving(true);
        try {
            const res = await fetch(`${API}/cycles/${cycleId}/tabs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
                body: JSON.stringify({ name: newTabName.trim() })
            });
            if (!res.ok) throw new Error();
            const created = await res.json();
            setLocalTabs(prev => [...prev, { ...created, decks: [] }]);
            setNewTabName('');
            await onRefresh();
            setToast({ message: 'Aba criada', type: 'success' });
        } catch {
            setToast({ message: 'Erro ao criar aba', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    // ── Renomear aba ──
    const handleRename = async (tab: Tab) => {
        if (!editingName.trim() || editingName === tab.name) {
            setEditingId(null);
            return;
        }
        try {
            await fetch(`${API}/cycles/${cycleId}/tabs/${tab._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
                body: JSON.stringify({ name: editingName.trim() })
            });
            setLocalTabs(prev => prev.map(t => t._id === tab._id ? { ...t, name: editingName.trim() } : t));
            setEditingId(null);
            await onRefresh();
            setToast({ message: 'Aba renomeada', type: 'success' });
        } catch {
            setToast({ message: 'Erro ao renomear', type: 'error' });
        }
    };

    // ── Deletar aba ──
    const handleDelete = async () => {
        if (!deletingId || deleteConfirm !== 'DELETAR') return;
        try {
            await fetch(`${API}/cycles/${cycleId}/tabs/${deletingId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token()}` }
            });
            setLocalTabs(prev => prev.filter(t => t._id !== deletingId));
            setDeletingId(null);
            setDeleteConfirm('');
            await onRefresh();
            setToast({ message: 'Aba deletada', type: 'success' });
        } catch {
            setToast({ message: 'Erro ao deletar', type: 'error' });
        }
    };

    // ── Drag & drop para reordenar ──
    const handleDragStart = (id: string) => setDragging(id);
    const handleDragOver = (e: React.DragEvent, id: string) => {
        e.preventDefault();
        setDragOver(id);
    };
    const handleDrop = async (targetId: string) => {
        if (!dragging || dragging === targetId) {
            setDragging(null);
            setDragOver(null);
            return;
        }
        const from = localTabs.findIndex(t => t._id === dragging);
        const to = localTabs.findIndex(t => t._id === targetId);
        const reordered = [...localTabs];
        const [moved] = reordered.splice(from, 1);
        reordered.splice(to, 0, moved);
        const withOrder = reordered.map((t, i) => ({ ...t, order: i }));
        setLocalTabs(withOrder);
        setDragging(null);
        setDragOver(null);

        try {
            await fetch(`${API}/cycles/${cycleId}/tabs-reorder`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
                body: JSON.stringify({ tabs: withOrder.map(t => ({ _id: t._id, order: t.order })) })
            });
            await onRefresh();
        } catch {
            setToast({ message: 'Erro ao reordenar', type: 'error' });
        }
    };

    const tabToDelete = localTabs.find(t => t._id === deletingId);

    return (
        <div className="tmm-overlay" onClick={onClose}>
            <div className="tmm-modal" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="tmm-header">
                    <h2>Gerenciar Abas</h2>
                    <button className="tmm-close" onClick={onClose}><X size={18} /></button>
                </div>

                {/* Criar nova aba */}
                <div className="tmm-create">
                    <input
                        className="tmm-input"
                        placeholder="Nome da nova aba..."
                        value={newTabName}
                        onChange={e => setNewTabName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleCreate()}
                    />
                    <button
                        className="tmm-btn-create"
                        onClick={handleCreate}
                        disabled={!newTabName.trim() || saving}
                    >
                        <Plus size={16} />
                        Criar
                    </button>
                </div>

                {/* Lista de abas */}
                <div className="tmm-list">
                    {localTabs.length === 0 && (
                        <div className="tmm-empty">Nenhuma aba criada ainda</div>
                    )}
                    {localTabs.map(tab => (
                        <div
                            key={tab._id}
                            className={`tmm-item ${dragOver === tab._id ? 'tmm-item--dragover' : ''} ${dragging === tab._id ? 'tmm-item--dragging' : ''}`}
                            draggable
                            onDragStart={() => handleDragStart(tab._id)}
                            onDragOver={e => handleDragOver(e, tab._id)}
                            onDrop={() => handleDrop(tab._id)}
                            onDragEnd={() => { setDragging(null); setDragOver(null); }}
                        >
                            <div className="tmm-item-drag">
                                <GripVertical size={16} />
                            </div>

                            {editingId === tab._id ? (
                                <input
                                    className="tmm-input tmm-input--inline"
                                    value={editingName}
                                    onChange={e => setEditingName(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') handleRename(tab);
                                        if (e.key === 'Escape') setEditingId(null);
                                    }}
                                    autoFocus
                                />
                            ) : (
                                <div className="tmm-item-info">
                                    <span className="tmm-item-name">{tab.name}</span>
                                    <span className="tmm-item-count">{tab.decks?.length || 0} decks</span>
                                </div>
                            )}

                            <div className="tmm-item-actions">
                                {editingId === tab._id ? (
                                    <button className="tmm-icon-btn tmm-icon-btn--confirm" onClick={() => handleRename(tab)}>
                                        <Check size={14} />
                                    </button>
                                ) : (
                                    <button className="tmm-icon-btn" onClick={() => { setEditingId(tab._id); setEditingName(tab.name); }}>
                                        <Pencil size={14} />
                                    </button>
                                )}
                                <button
                                    className="tmm-icon-btn tmm-icon-btn--danger"
                                    onClick={() => { setDeletingId(tab._id); setDeleteConfirm(''); }}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Confirmação de deleção inline */}
                {deletingId && tabToDelete && (
                    <div className="tmm-delete-confirm">
                        <p>Deletar <strong>"{tabToDelete.name}"</strong>? Os {tabToDelete.decks?.length || 0} decks vão para a próxima aba.</p>
                        <p className="tmm-delete-warn">Digite <strong>DELETAR</strong> para confirmar:</p>
                        <div className="tmm-delete-row">
                            <input
                                className="tmm-input"
                                placeholder="DELETAR"
                                value={deleteConfirm}
                                onChange={e => setDeleteConfirm(e.target.value)}
                                autoFocus
                            />
                            <button
                                className="tmm-btn-danger"
                                disabled={deleteConfirm !== 'DELETAR'}
                                onClick={handleDelete}
                            >
                                Confirmar
                            </button>
                            <button className="tmm-btn-cancel" onClick={() => { setDeletingId(null); setDeleteConfirm(''); }}>
                                Cancelar
                            </button>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default TabManagerModal;